import React, { useState } from 'react';
import { DateRangePreset } from '@shared/types';
import './DateRangeSelector.css';

interface DateRangeSelectorProps {
  onApply: (startDate: Date, endDate: Date, preset: DateRangePreset) => void;
  onCancel: () => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ onApply, onCancel }) => {
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Calculate date ranges based on preset
  const calculateDateRange = (preset: DateRangePreset): { start: Date; end: Date } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Midnight today

    switch (preset) {
      case 'today':
        return { start: today, end: now };

      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { start: yesterday, end: yesterdayEnd };

      case 'last-week':
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        return { start: lastWeek, end: now };

      case 'last-month':
        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return { start: lastMonth, end: now };

      case 'last-3-months':
        const last3Months = new Date(today);
        last3Months.setMonth(last3Months.getMonth() - 3);
        return { start: last3Months, end: now };

      case 'custom':
        // Will be handled separately
        return { start: new Date(), end: new Date() };

      default:
        return { start: today, end: now };
    }
  };

  // Validate date range
  const validateDateRange = (start: Date, end: Date): string | null => {
    const now = new Date();

    if (start >= end) {
      return 'Start date must be before end date';
    }

    if (end > now) {
      return 'End date cannot be in the future';
    }

    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > 365) {
      return 'Date range cannot exceed 365 days';
    }

    return null;
  };

  // Handle apply button click
  const handleApply = () => {
    console.log('🔵 [DATE SELECTOR] Apply button clicked, preset:', selectedPreset);
    let startDate: Date;
    let endDate: Date;

    if (selectedPreset === 'custom') {
      if (!customStartDate || !customEndDate) {
        setError('Please select both start and end dates');
        return;
      }

      startDate = new Date(customStartDate);
      endDate = new Date(customEndDate);
    } else {
      const range = calculateDateRange(selectedPreset);
      startDate = range.start;
      endDate = range.end;
    }

    console.log('🔵 [DATE SELECTOR] Date range:', { startDate, endDate });

    // Validate
    const validationError = validateDateRange(startDate, endDate);
    if (validationError) {
      console.error('🔵 [DATE SELECTOR] Validation error:', validationError);
      setError(validationError);
      return;
    }

    // Clear error and apply
    setError('');
    console.log('🔵 [DATE SELECTOR] Calling onApply...');
    onApply(startDate, endDate, selectedPreset);
  };

  // Format date for datetime-local input
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initialize custom dates with reasonable defaults
  React.useEffect(() => {
    if (selectedPreset === 'custom' && !customStartDate && !customEndDate) {
      const now = new Date();
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);

      setCustomStartDate(formatDateTimeLocal(weekAgo));
      setCustomEndDate(formatDateTimeLocal(now));
    }
  }, [selectedPreset, customStartDate, customEndDate]);

  return (
    <div className="date-range-selector-overlay">
      <div className="date-range-selector">
        <div className="date-range-header">
          <h3>Select Backtest Date Range</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <div className="date-range-body">
          <div className="preset-buttons">
            <button
              className={`preset-btn ${selectedPreset === 'today' ? 'active' : ''}`}
              onClick={() => setSelectedPreset('today')}
            >
              Today
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'yesterday' ? 'active' : ''}`}
              onClick={() => setSelectedPreset('yesterday')}
            >
              Yesterday
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'last-week' ? 'active' : ''}`}
              onClick={() => setSelectedPreset('last-week')}
            >
              Last Week
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'last-month' ? 'active' : ''}`}
              onClick={() => setSelectedPreset('last-month')}
            >
              Last Month
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'last-3-months' ? 'active' : ''}`}
              onClick={() => setSelectedPreset('last-3-months')}
            >
              Last 3 Months
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'custom' ? 'active' : ''}`}
              onClick={() => setSelectedPreset('custom')}
            >
              Custom
            </button>
          </div>

          {selectedPreset === 'custom' && (
            <div className="custom-date-inputs">
              <div className="date-input-group">
                <label>Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-input-group">
                <label>End Date & Time</label>
                <input
                  type="datetime-local"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
            </div>
          )}

          {selectedPreset !== 'custom' && (
            <div className="preset-info">
              {(() => {
                const range = calculateDateRange(selectedPreset);
                return (
                  <p>
                    From: <strong>{range.start.toLocaleString()}</strong>
                    <br />
                    To: <strong>{range.end.toLocaleString()}</strong>
                  </p>
                );
              })()}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="date-range-footer">
          <button className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="apply-btn" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangeSelector;
